import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';
import { toQuery, type AnalyticsQueryArgs } from './analyticsApiHelpers';

export type { AnalyticsQueryArgs } from './analyticsApiHelpers';
export { toQuery } from './analyticsApiHelpers';

export interface AnalyticsInsightHints {
  topDriverAgent?: string | null;
  topDriverSharePct?: number | null;
  tokensTotalPct?: number | null;
}

export interface BudgetUtilisationRow {
  applicationId?: string;
  name?: string;
  usedTokens: number;
  tokenCap?: number | null;
  utilisationPct?: number | null;
}

export interface AnalyticsSummary {
  tokensIn: number;
  tokensOut: number;
  reasoningTokens: number;
  tokensTotal: number;
  sessions: number;
  exchangeCount: number;
  activeAgents: number;
  activeApplications: number;
  budgetUtilisation?: {
    organisation?: {
      usedTokens: number;
      tokenCap?: number | null;
      utilisationPct?: number | null;
    };
    applications?: BudgetUtilisationRow[];
  };
  periodComparison?: {
    tokensTotalPct?: number | null;
    sessionsPct?: number | null;
  };
  insightHints?: AnalyticsInsightHints;
  from?: string;
  to?: string;
}

export interface TimeseriesPoint {
  bucket: string;
  value: number;
}

export interface AnalyticsTimeseries {
  grain: string;
  metric: string;
  points: TimeseriesPoint[];
  from?: string;
  to?: string;
}

export interface BreakdownRow {
  key: string;
  /** Display name (e.g. application name); falls back to key when absent. */
  label?: string;
  tokensTotal: number;
  tokensIn: number;
  tokensOut: number;
  exchangeCount: number;
}

export interface AnalyticsBreakdown {
  dimension: string;
  rows: BreakdownRow[];
  from?: string;
  to?: string;
}

export interface AnalyticsSessionRow {
  sessionId: string;
  tokensTotal: number;
  applicationId?: string;
  agentName?: string;
}

export interface AnalyticsSessions {
  sessions: AnalyticsSessionRow[];
  from?: string;
  to?: string;
}

export interface AnalyticsSessionDetail {
  sessionId: string;
  tokensIn: number;
  tokensOut: number;
  reasoningTokens: number;
  tokensTotal: number;
  models: string[];
  exchanges: Array<{
    exchangeId: string;
    model: string;
    agentName: string;
    applicationId?: string;
    inputTokens: number;
    outputTokens: number;
    reasoningTokens?: number;
    totalTokens: number;
    createdAt?: string;
    callRole?: string;
  }>;
}

export const analyticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAnalyticsSummary: builder.query<AnalyticsSummary, AnalyticsQueryArgs | void>({
      query: (args) => `/portal/analytics/summary${toQuery(args || {})}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyticsSummary>(response),
      providesTags: ['Analytics'],
    }),

    getAnalyticsTimeseries: builder.query<AnalyticsTimeseries, AnalyticsQueryArgs>({
      query: (args) => `/portal/analytics/timeseries${toQuery(args)}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyticsTimeseries>(response),
      providesTags: ['Analytics'],
    }),

    getAnalyticsBreakdown: builder.query<AnalyticsBreakdown, AnalyticsQueryArgs>({
      query: (args) => `/portal/analytics/breakdown${toQuery(args)}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyticsBreakdown>(response),
      providesTags: ['Analytics'],
    }),

    getAnalyticsSessions: builder.query<AnalyticsSessions, AnalyticsQueryArgs>({
      query: (args) => `/portal/analytics/sessions${toQuery(args)}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyticsSessions>(response),
      providesTags: ['Analytics'],
    }),

    getAnalyticsSession: builder.query<AnalyticsSessionDetail, string>({
      query: (sessionId) => `/portal/analytics/session/${sessionId}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyticsSessionDetail>(response),
      providesTags: ['Analytics'],
    }),
  }),
});

export const {
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsTimeseriesQuery,
  useGetAnalyticsBreakdownQuery,
  useGetAnalyticsSessionsQuery,
  useGetAnalyticsSessionQuery,
} = analyticsApi;
